/**
 * FluidShops Custom Admin Menu – Collapsible Groups
 *
 * This module enhances the WordPress admin menu by adding collapsible group functionality.
 * It allows users to collapse/expand menu sections for better organization and screen space management.
 * The module also implements custom menu pinning behavior that works with the collapsible groups.
 *
 * Features:
 * - Collapsible menu groups with smooth animations
 * - Smart group state management (remembers collapsed state)
 * - Automatic expansion of groups containing active menu items
 * - Responsive design support
 * - Custom menu pinning that works with dynamic height changes
 *
 * Dependencies:
 * - jQuery (required for DOM manipulation and events)
 */

(function (root, factory) {
	// UMD (Universal Module Definition) pattern for better compatibility
	// Supports AMD, CommonJS and browser globals
	if (typeof define === 'function' && define.amd) {
		define([], factory(root));
	} else if (typeof exports === 'object') {
		module.exports = factory(root);
	} else {
		root.CustomMenu = factory(root);
	}
})(this, function (root) {

	'use strict';

	var $ = jQuery;
	var _hasJQuery = ( $ != null );

	// Prevent multiple initializations of the module
	var _hasInitialized = false;

	// Object containing all public methods exposed by this module
	var _publicMethods = {};

	// Default configuration settings
	var _settings = {
		adminMenuSelector:                '#adminmenu',                       // Selector for the admin menu
		adminMenuWrapperSelector:         '#adminmenuwrap',                   // Selector for the admin menu wrapper

		groupLabelSelector:               'a.group-label',                    // Selector for group header links
		groupWrapperSelector:             'li',                               // Wrapper element for each group
		menuTitleSelector:                '.wp-menu-name',                    // Menu title element selector
		menuCollapseButtonSelector:       '#collapse-button',                 // WordPress menu collapse button
		currentItemSelector:              '.current',                         // Selector for active menu items
		groupHasSubmenuSelector:          '.wp-has-submenu',                  // Selector for menu items that have a submenu
		groupNotCurrentSubmenuClass:      'wp-not-current-submenu',           // Class for menu items that do not have a current submenu

		wpwrapSelector:                   '#wpwrap',                          // Selector for the wpwrap element
		adminbarSelector:                 '#wpadminbar',                      // Selector for the admin bar element

		toggleChevronClass:               'toggle-chevron',                   // Toggle chevron class
		iconSelector:                     '.dashicons',                       // Generic icon selector
		iconClass:                        'dashicons',                        // Generic icon class
		iconUpClass:                      'dashicons-arrow-up',               // Icon for expanded state
		iconDownClass:                    'dashicons-arrow-down',             // Icon for collapsed state
		collapsedClass:                   'collapsed',                        // Class added to collapsed groups
		currentMenuItemClass:             'current',                          // Class for active menu items
		stickyMenuClass:                  'sticky-menu',                      // Class for sticky menu

		debugMode:                        true,                               // Debug mode flag
	};
	var _currentMenuPinToContentState = 'fixed-top'; // Accepts: 'pinned-to-content', 'fixed-top', 'fixed-bottom'
	var _currentPinnedPosition = 0;
	var _currentScrollPosition = 0;
	var _currentScrollDirection = 'up'; // Accepts: 'up', 'down'
	var _currentMenuGroup = null;



	/**
	 * METHODS
	 */



	/**
	 * Return a function, that, when invoked, will only be triggered at most once
	 * during a given window of time. Normally, the throttled function will run
	 * as much as it can, without ever going more than once per `wait` duration;
	 * 
	 * @param   function  fn          Function to be executed.
	 * @param   int       threshhold  Wait time in milliseconds.
	 * @param   object    scope       Scope of the function to be executed.
	 * 
	 * @return  function              Function to be executed, incapsulated in a timed function.
	 */
	var throttle = function( fn, threshhold, scope ) {
		threshhold || (threshhold = 250);
		var last,
		deferTimer;
		return function () {
			var context = scope || this;
		
			var now = +new Date,
				args = arguments;
			if ( last && now < last + threshhold ) {
				// hold on to it
				clearTimeout( deferTimer );
				deferTimer = setTimeout( function () {
					last = now;
					fn.apply( context, args );
				}, threshhold );
			} else {
				last = now;
				fn.apply( context, args );
			}
		};
	}



	/**
	 * Collects all menu items that belong to a specific group.
	 * Items are collected from after the group header until the next group header
	 * or the menu collapse button.
	 *
	 * @param   Element  startItem  The first item in the group, which is the group label.
	 * 
	 * @returns {Array<Element>}  Array of elements representing group items
	 */
	var collectGroupedItems = function ( startItem ) {
		// Initialize variables
		var items = [];

		// Start with the next item
		var next = startItem.nextElementSibling;

		// Collect all items until the next group label
		while ( next ) {
			// Bail when we find another group label
			var isGroup = next.querySelector( _settings.groupLabelSelector );
			if ( isGroup ) { break; }

			// Bail when we find the collapse button
			var isCollapseButton = next.querySelector( _settings.menuCollapseButtonSelector );
			if ( isCollapseButton ) { break; }

			// Add the item to the list
			items.push( next );

			// Go to next item
			next = next.nextElementSibling;
		}

		return items;
	};


	/**
	 * Removes the current menu item classes from an item.
	 * 
	 * @param   Element  item  The item to remove the current menu item classes from.
	 */
	var removeCurrentMenuItemClassesFromItem = function( item ) {
		// Define list of current menu item classes to remove
		var currentClasses = [
			'current',
			'wp-has-current-submenu',
			'wp-menu-open',
		];

		// Remove current classes from the item
		currentClasses.forEach( function( className ) {
			// Remove class from item
			item.classList.remove( className );

			// Search for elements inside the item that have the current class
			var elements = item.querySelectorAll( '.' + className );
			elements.forEach( function( element ) {
				element.classList.remove( className );
			} );

			// Maybe add class `wp-not-current-submenu` to the item
			if ( item.matches( _settings.groupHasSubmenuSelector ) ) {
				item.classList.add( _settings.groupNotCurrentSubmenuClass );
			}
		} );
	}

	/**
	 * Removes the current menu item classes from all items in a group.
	 *
	 * @param   Element         groupLabel    The group label element
	 * @param   Array<Element>  groupedItems  Array of elements to remove the current menu item classes from
	 */
	var removeCurrentMenuItemClassesFromGroup = function( groupLabel, groupedItems ) {
		// Bail if jQuery is not available
		if ( ! _hasJQuery ) { return; }

		// Remove current classes from the group label
		removeCurrentMenuItemClassesFromItem( groupLabel );

		// Iterate over grouped items
		for ( var i = 0; i < groupedItems.length; i++ ) {
			// Get the item
			var item = groupedItems[ i ];

			// Remove current classes from the item
			removeCurrentMenuItemClassesFromItem( item );
		}
	}



	/**
	 * Checks if a menu group contains the currently active menu item.
	 * This is used to automatically expand groups containing the active page.
	 * 
	 * @param  Element  groupItem  The group container element to check
	 * 
	 * @returns {boolean} - True if the group contains the active item, false otherwise
	 */
	var isGroupActive = function( groupItem ) {
		// Get all items in the group
		var groupedItems = collectGroupedItems( groupItem );

		// Check if any item in the group is the currently selected item
		var hasCurrentItem = false;
		groupedItems.forEach( function( item ) {
			// Check if the item itself is current or contains a current item
			if ( item.matches( _settings.currentItemSelector ) || item.querySelector( _settings.currentItemSelector ) ) {
				hasCurrentItem = true;
			}
		} );

		// Return `true` if the group contains the active item, `false` otherwise
		return hasCurrentItem;
	};



	/**
	 * Toggles the visibility of a group's menu items.
	 * Handles show/hide animations for all items in a group.
	 *
	 * @param  {Array<Element>}  items       Array of elements to toggle
	 * @param  {boolean}         shouldHide  If true, hides the items; if false, shows them
	 */
	var toggleGroupedItems = function ( items, shouldHide ) {
		// Bail if jQuery is not available
		if ( ! _hasJQuery ) { return; }

		// Iterate over the items
		for ( var i = 0; i < items.length; i++ ) {
			// Get the item as a jQuery object
			var $item = $( items[ i ] );

			// Hide or show the item using the jQuery functions
			shouldHide ? $item.hide() : $item.show();
		}
	};



	/**
	 * Updates the toggle icon's direction based on group state.
	 * Manages the visual indicator for collapsed/expanded states.
	 *
	 * @param   Element  icon  The toggle icon element
	 * @param   boolean  isCollapsed  Current state of the group
	 */
	var updateIcon = function ( icon, isCollapsed ) {
		// Remove any arrow icon classes
		icon.classList.remove( _settings.iconUpClass, _settings.iconDownClass );

		// Add the appropriate class based on the collapsed state
		icon.classList.add( isCollapsed ? _settings.iconDownClass : _settings.iconUpClass );
	};



	/**
	 * Handles the complete toggle behavior when a group is clicked.
	 * This function:
	 * - Toggles group state
	 * - Handles scroll position adjustments
	 * - Updates UI elements
	 * - Triggers state change events
	 *
	 * @param {Element}         groupLabel  The group header link
	 * @param {Array<Element>}  items       The group's menu items
	 * @param {Element}         icon        The group's toggle icon
	 */
	var toggleGroup = function ( groupLabel, items, icon ) {
		// Toggle the collapsed class on the link
		groupLabel.classList.toggle( _settings.collapsedClass );
		var isCollapsed = groupLabel.classList.contains( _settings.collapsedClass );

		// Get the current scroll position and menu position
		var windowPos = window.scrollY;
		var menuWrapper = document.querySelector( _settings.adminMenuWrapperSelector );

		// Bail if the menu wrapper is not found
		if ( ! menuWrapper ) { return; }

		// Trigger event for menu height change before the state changes
		document.dispatchEvent( new CustomEvent( 'fs-menu-group-state-change-before', {
			bubbles: true,
			detail: {
				groupLabel: groupLabel,
				state: isCollapsed ? 'collapsed' : 'expanded'
			}
		} ) );

		// Toggle items and update icon
		requestAnimationFrame( function() {
			toggleGroupedItems( items, isCollapsed );
			updateIcon( icon, isCollapsed );
		} );

		// Trigger event for menu height change after the state changes
		document.dispatchEvent( new CustomEvent( 'fs-menu-group-state-change-after', {
			bubbles: true,
			detail: {
				groupLabel: groupLabel,
				state: isCollapsed ? 'collapsed' : 'expanded'
			}
		} ) );
	};



	/**
	 * Calculates the position to pin the menu to the content.
	 * 
	 * @returns {number}  The position to pin the menu to the content.
	 */
	var getPinnedPosition = function() {
		// Initialize variables
		var menuWrapper = document.querySelector( _settings.adminMenuWrapperSelector );
		var scrollPositionTop = window.scrollY;
		var viewportHeight = window.innerHeight;
		var menuHeight = menuWrapper.getBoundingClientRect().height;
		var adminbar = document.querySelector( _settings.adminbarSelector );
		var adminbarHeight = adminbar ? adminbar.getBoundingClientRect().height : 0;

		// Calculate pinned position
		var pinnedPosition = 0;
		
		// Calculate pinned position when scrolling UP
		if ( 'up' === _currentScrollDirection ) {
			pinnedPosition = scrollPositionTop + viewportHeight - menuHeight - adminbarHeight;
		}
		// Calculate pinned position when scrolling DOWN
		else {
			pinnedPosition = scrollPositionTop;
		}

		// Ensure pinned position is not negative, otherwise set to 0
		pinnedPosition = pinnedPosition >= 0 ? pinnedPosition : 0;

		// Return pinned position
		return pinnedPosition;
	};



	/**
	 * Change the elements style attributes to set the menu pinned state.	
	 * 
	 * @param  {string}  state           The state to set the menu to. Accepts: 'pinned-to-content', 'fixed-top', 'fixed-bottom', 'reset'.
	 * @param  {number}  pinnedPosition  (Optional) The position to pin the menu in relation to the content. Default current position of the top of the menu element in relation to the top of the page.
	 */
	_publicMethods.setMenuPinnedState = function( state, pinnedPosition ) {
		// Bail if state is not valid
		if ( ! [ 'pinned-to-content', 'fixed-top', 'fixed-bottom', 'reset' ].includes( state ) ) { return; }

		// Bail if state is already set
		if ( _currentMenuPinToContentState === state ) { return; }

		// Get menu wrapper
		var menuWrapper = document.querySelector( _settings.adminMenuWrapperSelector );

		// Bail if menu wrapper is not found
		if ( ! menuWrapper ) { return; }

		// Get admin bar height
		var adminbar = document.querySelector( _settings.adminbarSelector );
		var adminbarHeight = adminbar ? adminbar.getBoundingClientRect().height : 0;

		// Set state
		switch ( state ) {
			// PINNED TO CONTENT
			case 'pinned-to-content':
				// Get pinned position, or use from parameter if provided
				pinnedPosition = pinnedPosition || getPinnedPosition();
				
				// Set menu wrapper position
				menuWrapper.style.position = 'absolute';
				menuWrapper.style.top = pinnedPosition + 'px';
				menuWrapper.style.bottom = '';

				// Update pinned scroll position
				_currentPinnedPosition = pinnedPosition;
				break;

			// FIXED TO TOP
			case 'fixed-top':
				// Set menu wrapper position
				menuWrapper.style.position = 'fixed';
				menuWrapper.style.top = adminbarHeight + 'px';
				menuWrapper.style.bottom = '';
				break;

			// FIXED TO BOTTOM
			case 'fixed-bottom':
				// Set menu wrapper position
				menuWrapper.style.position = 'fixed';
				menuWrapper.style.top = '';
				menuWrapper.style.bottom = '0';
				break;

			// RESET
			case 'reset':
				// Reset menu wrapper position
				menuWrapper.style.position = '';
				menuWrapper.style.top = '';
				menuWrapper.style.bottom = '';
				break;
		}

		// Update current menu pinned state
		_currentMenuPinToContentState = state;
	};



	/**
	 * Changes the pinned menu state.
	 * Handles menu pinning behavior based on scroll position, window height, and menu height.
	 * Implements three states: fixed top, absolute top, and absolute bottom.
	 */
	var maybeChangePinnedMenuState = function() {
		// Get required elements
		var menuWrapper = document.querySelector( _settings.adminMenuWrapperSelector );
		var adminbar = document.querySelector( _settings.adminbarSelector );

		// Bail if required elements are not found
		if ( ! menuWrapper || ! adminbar ) { return; }

		// Initialize variables
		var scrollPositionTop = window.scrollY;
		var scrollPositionBottom = scrollPositionTop + window.innerHeight;
		var viewportHeight = window.innerHeight;
		var menuHeight = menuWrapper.getBoundingClientRect().height;
		var adminbarHeight = adminbar.getBoundingClientRect().height;
		var isMenuTallerThanViewport = ( menuHeight + adminbarHeight ) > viewportHeight;
		var isMobileView = window.matchMedia( '(max-width: 782px)' ).matches;

		// Get scroll direction
		var scrollDirection = scrollPositionTop > _currentScrollPosition ? 'down' : 'up';

		// Update current scroll position and direction
		_currentScrollPosition = scrollPositionTop;
		_currentScrollDirection = scrollDirection;

		// RESET
		// - Maybe unpin the menu for mobile view
		// - Maybe unpin the menu if the menu is shorter than the viewport
		if ( isMobileView || ! isMenuTallerThanViewport ) {
			_publicMethods.setMenuPinnedState( 'reset' );
			return;
		}

		// Calculate min and max scroll positions for the pinned state,
		// and whether the menu is at the top or bottom of the viewport
		var maxBottomScrollPosition = _currentPinnedPosition + menuHeight + adminbarHeight;
		var minTopScrollPosition = _currentPinnedPosition;
		var reachedMenuBottomPosition = scrollPositionBottom >= maxBottomScrollPosition;
		var reachedMenuTopPosition = scrollPositionTop <= minTopScrollPosition;

		// FIXED TO BOTTOM
		if ( reachedMenuBottomPosition && 'down' === scrollDirection ) {
			_publicMethods.setMenuPinnedState( 'fixed-bottom' );
			return;
		}

		// FIXED TO TOP
		if ( reachedMenuTopPosition && 'up' === scrollDirection ) {
			_publicMethods.setMenuPinnedState( 'fixed-top' );
			return;
		}

		// PINNED TO CONTENT
		if (
			( ! reachedMenuTopPosition && ! reachedMenuBottomPosition )   // Menu is not at the top or bottom of the viewport, it is in-between
			|| ( 'up' === scrollDirection && reachedMenuBottomPosition )  // Menu is at the bottom of the viewport, and scrolling up
			|| ( 'down' === scrollDirection && reachedMenuTopPosition )   // Menu is at the top of the viewport, and scrolling down
		) {
			_publicMethods.setMenuPinnedState( 'pinned-to-content' );
			return;
		}

		// Trigger event for menu position change
		document.dispatchEvent( new CustomEvent( 'fs-menu-position-changed', {
			bubbles: true,
			detail: {
				state: _currentMenuPinToContentState,
				scrollDirection: _currentScrollDirection,
			}
		} ) );
	};



	/**
	 * Handles click events on group labels.
	 * 
	 * @param  {Event}  event  The click event object
	 */
	var handleGroupLabelClick = function( event ) {
		// Get elements
		var groupLabel = event.target.closest( _settings.groupLabelSelector );
		var groupItem = groupLabel.closest( _settings.groupWrapperSelector );
		var groupedItems = collectGroupedItems( groupItem );
		var icon = groupLabel.querySelector( _settings.iconSelector );

		// Toggle the group
		toggleGroup( groupItem, groupedItems, icon );
	}

	/**
	 * Handles click events.
	 * 
	 * @param  {Event}  event  The click event object
	 */
	var handleClick = function( event ) {
		// GROUP LABEL
		if ( event.target.closest( _settings.groupLabelSelector ) ) {
			event.preventDefault();
			handleGroupLabelClick( event );
		}
	}



	/**
	 * Initializes a single menu group with collapsible functionality.
	 *
	 * @param   Element  $groupLabel  The group header link element
	 */
	var initializeGroup = function( groupLabel ) {
		// Get group item
		var groupItem = groupLabel.closest( _settings.groupWrapperSelector );

		// Bail if the group item is not found
		if ( ! groupItem ) { return; }

		// Get menu title element
		var menuTitle = groupItem.querySelector( _settings.menuTitleSelector );

		// Bail if the menu title is not found
		if ( ! menuTitle ) { return; }

		// Create icon
		var icon = document.createElement( 'span' );
		icon.classList.add( _settings.iconClass, _settings.toggleChevronClass );

		// Append icon element
		menuTitle.appendChild( icon );

		// Get initial state from element classes
		var isCollapsed = groupLabel.classList.contains( _settings.collapsedClass );

		// Expand the group if it contains the current page
		if ( ! _currentMenuGroup && isGroupActive( groupItem ) ) {
			isCollapsed = false;
			groupLabel.classList.remove( _settings.collapsedClass );
			_currentMenuGroup = groupItem;
		}

		// Get grouped items
		var groupedItems = collectGroupedItems( groupItem );

		// Set initial state
		toggleGroupedItems( groupedItems, isCollapsed );
		updateIcon( icon, isCollapsed );

		// Maybe remove current menu item classes from all items in the group
		if ( isCollapsed ) {
			removeCurrentMenuItemClassesFromGroup( groupLabel, groupedItems );
		}
	};

	/**
	 * Initializes all collapsible menu groups in the admin menu.
	 * This is the main setup function that:
	 * - Sets up each group with collapse/expand functionality
	 * - Initializes the menu collapse handler
	 * - Sets up event listeners for menu state changes
	 */
	var initializeGroups = function () {
		// Get all group labels
		var groupLabels = document.querySelectorAll( _settings.groupLabelSelector );

		// Initialize each group
		for ( var i = 0; i < groupLabels.length; i++ ) {
			initializeGroup( groupLabels[ i ] );
		}

		// Add event listeners
		document.addEventListener( 'click', handleClick, true );
	};


	/**
	 * Initialize the custom menu pinning functionality.
	 */
	var initCustomMenuPinning = function() {
		// Bail if jQuery is not available
		if ( ! _hasJQuery ) { return; }

		// Add class `sticky-menu` to the body
		// This is necessary to make sure other scripts or styles that depend on this class are applied
		document.body.classList.add( _settings.stickyMenuClass );

		// Throttle the maybeChangePinnedMenuState function to 16ms (60fps)
		var throttledMaybeChangePinnedMenuState = throttle( maybeChangePinnedMenuState, 16 );

		// Add event listeners
		document.addEventListener( 'fs-menu-group-state-change-after', throttledMaybeChangePinnedMenuState );
		document.addEventListener( 'scroll', throttledMaybeChangePinnedMenuState );

		// Initial pinning check
		maybeChangePinnedMenuState();
	};



	/**
	 * Initializes the collapsible menu functionality.
	 * This is the main entry point for the module.
	 *
	 * @param {Object} options - Optional configuration overrides
	 *                          See _settings for available options
	 */
	_publicMethods.init = function( options ) {
		// Prevent re-initialization
		if ( _hasInitialized ) { return; }

		// Merge provided options with defaults
		_settings = $.extend( {}, _settings, options || {} );

		// Initialize collapsible groups
		initializeGroups();

		// Initialize menu pinning (sticky state)
		initCustomMenuPinning();

		// Set initialized flag
		_hasInitialized = true;
	};

	// Return public API
	return _publicMethods;
} );
